import React from "react";
import { api, setToken, clearToken, isAuthed } from "@/lib/api";

const AuthContext = React.createContext({
  user: null,
  loading: true,
  badges: [],
  refresh: async () => {},
  login: async () => {},
  register: async () => {},
  logout: () => {},
  setUser: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [badges, setBadges] = React.useState([]);

  const refresh = React.useCallback(async () => {
    if (!isAuthed()) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/user/me");
      setUser(data);
    } catch {
      // Don't clear the token here — admin tokens fail /auth/user/me (student-only
      // endpoint) but remain valid for /admin/* routes. We just unset the
      // student-facing user state; if the token is truly invalid, the next
      // API call will return 401 and the user can log in again.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    api.get("/badges").then((r) => setBadges(r.data)).catch(() => {});
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login-user", { email, password });
    setToken(data.access_token);
    await refresh();
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setToken(data.access_token);
    await refresh();
  };

  const logout = () => { clearToken(); setUser(null); };

  return (
    <AuthContext.Provider value={{ user, loading, badges, refresh, login, register, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return React.useContext(AuthContext); }

export function useBadgeMap() {
  const { badges } = useAuth();
  return React.useMemo(() => Object.fromEntries(badges.map((b) => [b.slug, b])), [badges]);
}
